export interface QuickReply {
  id: string;
  text: string;
  icon?: string;
}

export interface GenerateIntentQuickRepliesParams {
  displayName?: string;
  action?: string;
  description?: string;
  responseText?: string;
  trainingPhrases?: string[];
  experienceName?: string;
  experienceCity?: string;
  count?: number;
}

/**
 * Genera quick replies automaticamente usando endpoint server-side.
 */
export const generateQuickReplies = async (
  title: string,
  location: string,
  description: string
): Promise<QuickReply[]> => {
  try {
    console.log('🤖 Generazione quick replies via API server...');

    const response = await fetch('/api/generate-quick-replies', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title,
        location,
        description,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('⚠️ API quick replies non disponibile:', errorText);
      return getDefaultQuickReplies();
    }

    const result = await response.json();
    const quickReplies = result?.quickReplies;

    if (!Array.isArray(quickReplies) || quickReplies.length === 0) {
      return getDefaultQuickReplies();
    }

    return quickReplies.slice(0, 6);
  } catch (error) {
    console.error('❌ Errore nella generazione quick replies:', error);
    return getDefaultQuickReplies();
  }
};

/**
 * Genera quick replies AI contestuali per un singolo intent/interazione.
 * Il backend (Gemini) riconosce la tipologia (POI, gastronomia, storia, start/welcome, evento, ecc.)
 * e produce quick replies in prima persona utente, con emoji iniziale.
 */
export const generateIntentQuickReplies = async (
  params: GenerateIntentQuickRepliesParams,
): Promise<QuickReply[]> => {
  try {
    const response = await fetch('/api/generate-intent-quick-replies', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        displayName: params.displayName || '',
        action: params.action || '',
        description: params.description || '',
        responseText: params.responseText || '',
        trainingPhrases: Array.isArray(params.trainingPhrases) ? params.trainingPhrases : [],
        experienceName: params.experienceName || '',
        experienceCity: params.experienceCity || '',
        count: Math.max(3, Math.min(6, Number(params.count) || 4)),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('⚠️ API intent quick replies non disponibile:', errorText);
      return getDefaultQuickReplies().slice(0, params.count || 4);
    }

    const result = await response.json();
    const quickReplies = result?.quickReplies;

    if (!Array.isArray(quickReplies) || quickReplies.length === 0) {
      return getDefaultQuickReplies().slice(0, params.count || 4);
    }

    return quickReplies.slice(0, params.count || 4);
  } catch (error) {
    console.error('❌ Errore nella generazione intent quick replies:', error);
    return getDefaultQuickReplies().slice(0, params.count || 4);
  }
};

/**
 * Quick replies di default in caso di errore
 */
const getDefaultQuickReplies = (): QuickReply[] => [
  {
    id: 'qr-1',
    text: '📚 Raccontami la storia di questo luogo',
    icon: 'book'
  },
  {
    id: 'qr-2',
    text: '👀 Cosa posso visitare qui',
    icon: 'eye'
  },
  {
    id: 'qr-3',
    text: '🍝 Quali sono i piatti tipici',
    icon: 'restaurant'
  },
  {
    id: 'qr-4',
    text: '✨ Ci sono leggende locali',
    icon: 'sparkles'
  },
  {
    id: 'qr-5',
    text: '📅 Quando visitare',
    icon: 'calendar'
  },
  {
    id: 'qr-6',
    text: '🎭 Eventi e tradizioni',
    icon: 'calendar-star'
  }
];
